import { Vector3 } from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { SampledPath } from './sampledPath'

/**
 * The temporary curve a bus merges along — port of
 * `SplineController.FindNearestFlowAlignedEntry` -> `SmoothJoinEntry` ->
 * `CreateEntrySpline`.
 *
 * A merging bus does not teleport onto the road. The original builds a
 * throwaway `SplineComputer` per bus: a rounded corner out of the parking lot,
 * then the road itself from the entry point onward. The bus drives that curve
 * until its tail clears the join, at which point `CheckStateTransition` swaps it
 * onto the main spline and destroys the entry spline.
 *
 * Because the tail section *is* the road, the handover is a plain offset: once
 * the bus is past `joinDistance`, distance along this curve maps one-to-one
 * onto distance along the road.
 *
 * This is the only thing the player directly steers, so it is ported closely.
 */

/**
 * Ghidra dropped the `set_type` argument in `CreateEntrySpline`, so the spline
 * type has to be reasoned out. It is **Catmull-Rom**.
 *
 * This was read as Linear for a while, on the grounds that `SmoothJoinEntry`
 * already solves an exact fillet and that fitting a curve back through it
 * breaks the tangency it solved for. That reasoning weighed a ~0.9 degree
 * overshoot at the arc ends against a defect an order of magnitude worse, and
 * three things in the decompile settle it the other way:
 *
 *  - **Control points come out 0.9 units apart** (`count = max(4, round(total /
 *    0.9))`). Driven as a polygon that is a 23-degree break per chord around a
 *    2.2-radius fillet. 0.9 is control-point spacing for something that gets
 *    smoothed, not chord spacing for something that does not.
 *  - **`set_knotParametrization(spline, 0)`** is called immediately before
 *    `SetPoints`. In Dreamteck that property is the Catmull-Rom knot alpha
 *    (0 = uniform) and has no effect on any other type; calling it on a Linear
 *    spline would be dead code.
 *  - **The join descends in Y** — `busFront.y * 0.5` for the lead-in midpoint,
 *    then `0` across the arc. Buses park at y = 1 and the road sits at y = 0
 *    (`boxPosition.y` in the level data), so those three values are a ramp
 *    written for a curve to round off. As a polygon they are a cliff: measured
 *    47-56 degrees on level 1, in Y alone, which is the kink visible in the
 *    overlay while every plan-view check passed.
 *
 * Catmull-Rom also interpolates its control points, so the road-following tail
 * still lands *on* the road — which is what makes the handover in
 * `CheckStateTransition` a plain distance offset rather than a re-projection.
 * A B-spline would miss the points and break that.
 *
 * The type and sample rate live on `SampledPath`, shared with the exit curve.
 */

/** `CreateEntrySpline` walks the road forward in 1.5-unit steps. */
const ROAD_STEP = 1.5
/** Open splines stop before the very end. */
const ROAD_END_LIMIT = 0.995

/**
 * 🔍 The hard ceiling on the point list — `if (199 < list.Count) goto done`
 * (`CreateEntrySpline__0x212987c.c`:204). It bounds the straight run-on as well as
 * the sampled part.
 */
const MAX_ENTRY_POINTS = 200
/** Control-point spacing for the uniform resample: `round(total / 0.9)`. */
const RESAMPLE_SPACING = 0.9

/** 🔍 Floor and margin of the road tail — `__0x212987c.c`:189-192. */
const ROAD_TAIL_FLOOR = 30
const ROAD_TAIL_MARGIN = 20

/**
 * 🔍 How much road the entry curve carries past the join.
 *
 * `SplineController$CreateEntrySpline__0x212987c.c`:189-192, with
 * `fVar20 = CalculateLength(_mainSpline)` and `param_8` the bus size percent:
 *
 *     fVar21 = (float)((double)fVar20 * param_8) + 20.0;
 *     fVar37 = 30.0;
 *     if (30.0 <= fVar21) fVar37 = fVar21;
 *
 * i.e. `Max(30, busLengthWorldUnits + 20)` — `splineLength * percent` is exactly
 * the product `CheckBusRequests` (`__0x212781c.c`:326) passes to `Initialize` as
 * `_busLengthWorldUnits`, so the tail is always the bus plus 20 units of room,
 * and never shorter than 30.
 *
 * The 20-unit margin is what makes `CheckStateTransition`'s tail rule reachable:
 * the bus can put its whole body past the join with room to spare, instead of
 * running out of curve and taking the `_headPercent >= 0.985` escape.
 */
export function entryRoadTailLength(splineLength: number, targetLengthPercent: number): number {
  return Math.max(ROAD_TAIL_FLOOR, splineLength * targetLengthPercent + ROAD_TAIL_MARGIN)
}

export class EntryPath extends SampledPath {
  /** Arc distance at which the curve meets the road. */
  readonly joinDistance: number
  /** Arc distance along the road that `joinDistance` corresponds to. */
  readonly roadJoinDistance: number
  /** Index of the last join sample, for debug drawing. */
  readonly joinIndex: number
  /**
   * The fillet that produced this curve, for `npm run debug:join`.
   *
   * Carried on the path rather than recomputed by the debug script, because the
   * geometry depends entirely on *where the bus stood when it merged* — which is
   * at the kerb after its drive-out, not in the parking slot. Rebuilding the
   * case from a parked bus measures a curve the game never drives.
   */
  readonly joinDebug: JoinDebug | undefined

  constructor(
    controlPoints: Vector3[],
    joinControlIndex: number,
    roadJoinDistance: number,
    joinDebug?: JoinDebug,
  ) {
    super(controlPoints)
    this.roadJoinDistance = roadJoinDistance
    this.joinDebug = joinDebug

    const clampedJoin = Math.min(
      Math.max(0, joinControlIndex),
      Math.max(0, controlPoints.length - 1),
    )
    this.joinIndex = this.sampleIndexOfControlPoint(clampedJoin)
    this.joinDistance = this.cumulative[this.joinIndex]
  }
}

// ---------------------------------------------------------------------------
// FindNearestFlowAlignedEntry
// ---------------------------------------------------------------------------

/**
 * Port of `SplineController.FindNearestFlowAlignedEntry`.
 *
 *     for (p = 0.02; p <= maxEntryPercent; p += 0.003):
 *         delta   = spline.Evaluate(p).position - busFront          (XZ only)
 *         nearest by |delta| is remembered as a fallback
 *         ahead   = dot(parkingForward, delta)
 *         if (ahead >= 0.3):
 *             lateral = |delta - parkingForward * ahead|
 *             if (lateral <= 2.8 && ahead < bestAhead) -> take p
 *     return aligned ?? nearest ?? 0.02
 *
 * "Flow aligned" is not about tangent direction: it means the point lies on the
 * ray the bus is already driving down, within 2.8 units of it — where the nose
 * would meet the road if it kept going straight.
 */
export function findNearestFlowAlignedEntry(
  road: RoadSpline,
  busFront: Vector3,
  parkingForward: Vector3,
  maxEntryPercent: number,
): number {
  const { spline } = road
  const sample = new Vector3()

  let nearest = -1
  let nearestDistance = Number.MAX_VALUE
  let aligned = -1
  let bestAhead = Number.MAX_VALUE

  for (let p = 0.02; p <= maxEntryPercent; p += 0.003) {
    spline.evaluatePosition(p, sample)
    const dx = sample.x - busFront.x
    const dz = sample.z - busFront.z

    const distance = Math.hypot(dx, dz)
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearest = p
    }

    const ahead = parkingForward.x * dx + parkingForward.z * dz
    if (ahead < 0.3) continue

    const lateral = Math.hypot(dx - parkingForward.x * ahead, dz - parkingForward.z * ahead)
    if (lateral <= 2.8 && ahead < bestAhead) {
      bestAhead = ahead
      aligned = p
    }
  }

  if (aligned >= 0) return aligned
  return nearest >= 0 ? nearest : 0.02
}

// ---------------------------------------------------------------------------
// BuildBridgeSectionPoints
// ---------------------------------------------------------------------------

/**
 * `SplineController.BuildBridgeSectionPoints(busFrontPos, entryPos)`.
 *
 *     count = clamp((int)Vector3.Distance(busFront, entryPos), 2, 6)
 *     for i in 1..count: points[i-1] = Lerp(busFront, entryPos, clamp01(i / count))
 *
 * The loop starts at 1, so the bus's own front is not repeated as a point.
 */
export function buildBridgeSectionPoints(busFront: Vector3, entryPos: Vector3): Vector3[] {
  const distance = busFront.distanceTo(entryPos)
  let count = Math.trunc(distance)
  if (!Number.isFinite(count)) count = 2
  if (count > 5) count = 6
  if (count < 3) count = 2

  const out: Vector3[] = []
  for (let i = 1; i <= count; i++) {
    out.push(new Vector3().lerpVectors(busFront, entryPos, Math.max(0, Math.min(1, i / count))))
  }
  return out
}

// ---------------------------------------------------------------------------
// SmoothJoinEntry
// ---------------------------------------------------------------------------

export interface JoinResult {
  /** The (possibly refined) entry percent on the road. */
  percent: number
  /** Points from just past the bus's nose up to and including the road point. */
  joinPoints: Vector3[]
  /** Intermediate geometry, for `npm run debug:join`. */
  debug?: JoinDebug
}

export interface JoinDebug {
  kind: 'fillet' | 'parallel' | 'no-corner'
  angle: number
  cross: number
  distBus: number
  distRoad: number
  radius: number
  tangentLen: number
  corner: Vector3
  tanIn: Vector3
  tanOut: Vector3
  centre: Vector3
  roadPos: Vector3
  roadDir: Vector3
  arcSteps: number
  /** Where the generated arc actually ends — should land on `tanOut`. */
  arcEnd: Vector3
}

/**
 * Port of `SplineController.SmoothJoinEntry`.
 *
 * This is the step that makes the merge look driven rather than snapped, and it
 * is a proper geometric fillet, not a lerp. Walking forward along the road in
 * 0.75-unit steps (up to 10 units), for each candidate it:
 *
 *   - takes the road tangent there and the angle to the bus's heading;
 *   - if the two are within 0.14 rad it gives up on a corner and returns a
 *     straight bridge — they are already parallel;
 *   - otherwise intersects the bus's ray with the road line to find the corner,
 *     and sizes the largest fillet that fits:
 *
 *         radius = min(min(distToCorner, distCornerToRoad) * 0.95 / tan(angle/2), 2.5)
 *
 *   - keeps the best radius, stopping early once one reaches 2.0.
 *
 * It then emits: an optional midpoint, an arc of 3–8 segments around the fillet
 * centre, another optional midpoint, and the road point itself.
 */
export function smoothJoinEntry(
  road: RoadSpline,
  basePercent: number,
  busFront: Vector3,
  parkingForward: Vector3,
  maxEntryPercent: number,
): JoinResult {
  const { spline } = road
  const splineLength = spline.length

  const roadPos = new Vector3()
  const roadDir = new Vector3()

  let best: {
    percent: number
    radius: number
    angle: number
    cross: number
    corner: Vector3
    roadPos: Vector3
    roadDir: Vector3
    distBus: number
    distRoad: number
  } | null = null

  for (let walk = 0; walk <= 10; walk += 0.75) {
    const p = basePercent + walk / splineLength
    if (p > maxEntryPercent) break

    spline.evaluatePosition(p, roadPos)
    spline.evaluateTangent(p, roadDir)
    roadDir.y = 0
    if (roadDir.x * roadDir.x + roadDir.z * roadDir.z < 0.001) continue
    roadDir.normalize()

    const dot = Math.max(
      -1,
      Math.min(1, parkingForward.x * roadDir.x + parkingForward.z * roadDir.z),
    )
    const angle = Math.acos(dot)

    if (angle < 0.14) {
      // Already parallel enough — no corner to round.
      return {
        percent: p,
        joinPoints: buildBridgeSectionPoints(busFront, roadPos.clone()),
        debug: parallelDebug(angle, roadPos, roadDir),
      }
    }

    const cross = parkingForward.x * roadDir.z - parkingForward.z * roadDir.x
    if (Math.abs(cross) < 0.05) continue

    // Where the bus's ray meets the road's line.
    const distBus =
      ((roadPos.z - busFront.z) * roadDir.x - (roadPos.x - busFront.x) * roadDir.z) / -cross
    if (distBus < 0.4) continue

    const corner = busFront.clone().addScaledVector(parkingForward, distBus)
    const distRoad = roadDir.x * (roadPos.x - corner.x) + roadDir.z * (roadPos.z - corner.z)
    if (distRoad < 0.4) continue

    const radius = Math.min(
      (Math.min(distBus, distRoad) * 0.95) / Math.tan(angle * 0.5),
      2.5,
    )
    if (radius < 0.15 || (best !== null && radius <= best.radius)) continue

    best = {
      percent: p,
      radius,
      angle,
      cross,
      corner,
      roadPos: roadPos.clone(),
      roadDir: roadDir.clone(),
      distBus,
      distRoad,
    }
    if (radius >= 2.0) break
  }

  if (best === null) {
    const pos = spline.evaluatePosition(basePercent)
    return {
      percent: basePercent,
      joinPoints: buildBridgeSectionPoints(busFront, pos),
      debug: { ...parallelDebug(0, pos, new Vector3(0, 0, 1)), kind: 'no-corner' },
    }
  }

  const tangentLen = Math.tan(best.angle * 0.5) * best.radius
  const tanIn = best.corner.clone().addScaledVector(parkingForward, -tangentLen)
  const tanOut = best.corner.clone().addScaledVector(best.roadDir, tangentLen)

  // Perpendicular toward the arc centre; which side depends on the turn.
  const perp =
    best.cross < 0
      ? new Vector3(parkingForward.z, 0, -parkingForward.x)
      : new Vector3(-parkingForward.z, 0, parkingForward.x)
  const centre = tanIn.clone().addScaledVector(perp, best.radius)

  const points: Vector3[] = []

  // Optional lead-in midpoint, if the straight before the arc is long enough.
  //
  // Y is halved rather than interpolated: the original writes
  // `busFront.y + (0 - busFront.y) * 0.5`, the arc points get `y = 0` outright,
  // and the lead-out midpoint gets `roadPos.y * 0.5`. The join is what walks the
  // bus down from its parked height onto the road.
  if (best.distBus - tangentLen > 1.2) {
    points.push(
      new Vector3(
        busFront.x + (tanIn.x - busFront.x) * 0.5,
        busFront.y * 0.5,
        busFront.z + (tanIn.z - busFront.z) * 0.5,
      ),
    )
  }

  // Arc: 3..8 segments, roughly one per 0.8 units of arc length.
  let steps = Math.trunc((best.angle * best.radius) / 0.8)
  if (!Number.isFinite(steps)) steps = 3
  if (steps > 7) steps = 8
  if (steps < 4) steps = 3

  // These chords are knots of the entry spline, not the drawn curve, so the
  // original's 3–8 of them is the right count: the Catmull-Rom rounds them back
  // into an arc. (An earlier pass multiplied this by four to hide the polygon a
  // Linear reading produced — see ENTRY_SPLINE_TYPE.)
  const arcSteps = steps

  const startAngle = Math.atan2(tanIn.z - centre.z, tanIn.x - centre.x)
  // The decompile carries the sweep as `cross < 0 ? +angle : -angle` but then
  // rotates with a negated sine — `x = c.x + v.x*cos + v.z*(-sin)` — so the net
  // rotation is by the opposite sign. Applying the raw sweep with a standard
  // rotation sends the arc to the mirror point on the far side of the centre,
  // which is what `npm run debug:join` caught: arcEnd landed 4.9 units from
  // tanOut, exactly mirrored about the centre.
  const sweep = best.cross < 0 ? -best.angle : best.angle
  const arcEnd = new Vector3()
  for (let i = 0; i <= arcSteps; i++) {
    const a = startAngle + (sweep * i) / arcSteps
    const point = new Vector3(
      centre.x + Math.cos(a) * best.radius,
      0,
      centre.z + Math.sin(a) * best.radius,
    )
    points.push(point)
    if (i === arcSteps) arcEnd.copy(point)
  }

  // Optional lead-out midpoint.
  if (best.distRoad - tangentLen > 1.2) {
    points.push(
      new Vector3(
        tanOut.x + (best.roadPos.x - tanOut.x) * 0.5,
        best.roadPos.y * 0.5,
        tanOut.z + (best.roadPos.z - tanOut.z) * 0.5,
      ),
    )
  }
  points.push(best.roadPos.clone())

  return {
    percent: best.percent,
    joinPoints: points,
    debug: {
      kind: 'fillet',
      angle: best.angle,
      cross: best.cross,
      distBus: best.distBus,
      distRoad: best.distRoad,
      radius: best.radius,
      tangentLen,
      corner: best.corner.clone(),
      tanIn: tanIn.clone(),
      tanOut: tanOut.clone(),
      centre: centre.clone(),
      roadPos: best.roadPos.clone(),
      roadDir: best.roadDir.clone(),
      arcSteps: steps,
      arcEnd: arcEnd.clone(),
    },
  }
}

function parallelDebug(angle: number, roadPos: Vector3, roadDir: Vector3): JoinDebug {
  const zero = new Vector3()
  return {
    kind: 'parallel',
    angle,
    cross: 0,
    distBus: 0,
    distRoad: 0,
    radius: 0,
    tangentLen: 0,
    corner: zero.clone(),
    tanIn: zero.clone(),
    tanOut: zero.clone(),
    centre: zero.clone(),
    roadPos: roadPos.clone(),
    roadDir: roadDir.clone(),
    arcSteps: 0,
    arcEnd: zero.clone(),
  }
}

// ---------------------------------------------------------------------------
// CreateEntrySpline
// ---------------------------------------------------------------------------

/**
 * Port of `SplineController.CreateEntrySpline`.
 *
 * Assembles the control points — the bus's own tail and nose, the join points
 * from `SmoothJoinEntry`, then the road sampled forward every 1.5 units — and
 * resamples the whole thing uniformly by arc length before handing it to the
 * spline, exactly as the original does with its `List<float>` cumulative table.
 *
 * 🔍 `targetLengthPercent` is the bus's `GetSplineLengthPercent` — the same
 * 0.05 / 0.075 / 0.125 that `CheckBusRequests` multiplies by the road length to
 * get `_busLengthWorldUnits` (`__0x212781c.c`:326 passes exactly that product as
 * `Initialize`'s first argument, and :323 hands this function the bare percent).
 *
 * The road tail is then floored at 30 units — `__0x212987c.c`:189-192:
 *
 *     fVar21 = (float)((double)fVar20 * param_8) + 20.0;   // busLen + 20
 *     fVar37 = 30.0;
 *     if (30.0 <= fVar21) fVar37 = fVar21;                 // Max(30, busLen + 20)
 *
 * with `fVar20 = CalculateLength(_mainSpline)`. So even the shortest bus on the
 * shortest loop gets 30 units of road ahead of the join, and a Large on a long
 * loop gets `busLength + 20`.
 */
/**
 * The road tail of `CreateEntrySpline` — `__0x212987c.c`:199-296.
 *
 * Split out of `createEntrySpline` so the walk can be measured on its own. What
 * the caller can see afterwards is the **resampled** curve (0.9-unit control
 * points chosen by nearest arc length), and that blurs every distance in here by
 * up to half a spacing — enough to hide the difference this loop is about. The
 * points go straight into `points`, which is the original's `List<Vector3>`.
 *
 * The walk:
 *
 *     while (travelled < roadTailLength):
 *         if (199 < points.Count) break                       // :204
 *         next = extrapolating ? prev + dir * 1.5             // :208-210
 *                              : spline.Evaluate(percent += 1.5 / length)
 *         if (!closed && percent >= 0.995) extrapolating = true   // :219
 *         if (0.0001 < (next - prev).sqrMagnitude XZ)             // :232
 *             dir = ((next - prev) with y dropped).normalized     // :255-258
 *         points.Add(next)                                        // :283-285
 *         travelled += |next - prev|                              // :291-293
 *         prev = next
 *
 * @returns how far the walk actually got, which is the loop's own `travelled`.
 */
export function walkEntryRoadTail(
  spline: RoadSpline['spline'],
  startPercent: number,
  points: Vector3[],
  roadTailLength: number,
): number {
  const splineLength = spline.length

  // 🔍 At the end of an open spline the original does **not** stop, and does not
  // wrap either — it **runs on straight** (`:207-219`). The jump at `:219` sets
  // a latch, and from then on `:208-210` produces each next point as
  //
  //     next = prev + dir * 1.5f
  //
  // with `dir` the flattened direction of the previous step, until the tail is
  // as long as `roadTailLength` asked for. The latch is never cleared, so once
  // the walk leaves the spline it never comes back to it.
  //
  // This port used to `break` here, and the comment that stood in this place
  // weighed wrap against break and picked break. Both were wrong: wrapping does
  // pin every further sample to the end of the spline (that part was right and
  // is why it was rejected), but breaking truncates the tail. On level 12 the
  // entry sits near the end of the road and the tail came out **11.70 units**
  // for a bus **19.19** long, so the tail-based handover could not run at all
  // and the bus fell through to the `_headPercent >= 0.985` escape.
  //
  // Running on straight matches the road itself: `PORTING.md` records that the
  // spline is open with two straight stubs at its ends.
  let percent = startPercent
  let travelled = 0
  // `Vector3 dir` is live across iterations in the original and is only ever
  // written from a completed step, so an extrapolation that begins on the very
  // first step has nothing to go on and emits a stack of identical points. That
  // needs `startPercent` itself to already be within 1.5 units of the end;
  // `npm run check:entrytail` reports it happening on 0 of 40 levels.
  const dir = new Vector3()
  let prev = points[points.length - 1]
  let extrapolating = false

  while (travelled < roadTailLength) {
    // `if (199 < list.Count) goto done` (`:204`) — a hard 200-point ceiling on
    // the whole list, tail and nose and join points included.
    if (points.length > MAX_ENTRY_POINTS - 1) break

    let next: Vector3
    if (extrapolating) {
      next = prev.clone().addScaledVector(dir, ROAD_STEP)
    } else {
      percent += ROAD_STEP / splineLength
      if (percent >= ROAD_END_LIMIT) {
        extrapolating = true
        next = prev.clone().addScaledVector(dir, ROAD_STEP)
      } else {
        next = spline.evaluatePosition(percent)
      }
    }

    // 🔍 `:291-293` — `travelled` grows by the **chord actually walked**, not by
    // the 1.5 that was asked for:
    //
    //     travelled += Mathf.Sqrt((pz - nz)*(pz - nz) + (px - nx)*(px - nx)
    //                             + (py - ny)*(py - ny));
    //
    // It is a **3D** distance in the decompile — all three components are
    // squared — so it is a 3D distance here. But that half of it is **not
    // observable**: every one of the 40 roads is exactly flat (y spread 0 to the
    // last bit, measured over 400 samples each), and the extrapolated stretch
    // steps along a `dir` with y already zeroed. Swapping this for a plain XZ
    // distance changes nothing on any shipped level, and no test can tell them
    // apart. It stays 3D because that is what the original does, not because
    // anything here proves it.
    //
    // What *is* observable is chord-vs-step. The two agree on the extrapolated
    // stretch, where `next` is `prev + dir * 1.5` and the chord is exactly 1.5.
    // On the sampled stretch they do not: `+= ROAD_STEP` let the bookkeeping run
    // ahead of the walk through a bend and stopped the loop before
    // `roadTailLength` had really been covered — 256 of 4800 walks came out
    // short, the worst by 0.473 units.
    travelled += next.distanceTo(prev)

    // `dir = ((next - prev) with y dropped).normalized`, and a step too short to
    // have a direction leaves the old one standing rather than zeroing it —
    // the `if (0.0001 < sqrMagnitude)` at `:229` guards the whole update.
    const dx = next.x - prev.x
    const dz = next.z - prev.z
    const sq = dx * dx + dz * dz
    if (sq > 0.0001) {
      const inv = 1 / Math.sqrt(sq)
      dir.set(dx * inv, 0, dz * inv)
    }
    points.push(next)
    prev = next
  }

  return travelled
}

export function createEntrySpline(
  road: RoadSpline,
  tail: Vector3,
  nose: Vector3,
  join: JoinResult,
  targetLengthPercent: number,
): EntryPath {
  const { spline } = road
  const splineLength = spline.length
  const roadTailLength = entryRoadTailLength(splineLength, targetLengthPercent)
  const roadJoinDistance = spline.distanceAt(join.percent)

  const raw: Vector3[] = [tail.clone(), nose.clone(), ...join.joinPoints]
  const joinRawIndex = raw.length - 1

  walkEntryRoadTail(spline, join.percent, raw, roadTailLength)

  // Uniform arc-length resample before the curve is built.
  //
  // The original accumulates a `List<float>` of cumulative distances and then
  // walks it at `total * i / count`, and this step matters: Dreamteck percent is
  // uniform per *segment*, not per unit length, so stepping it by
  // `1.5 / splineLength` produces control points at wildly uneven spacing.
  // Feeding those straight to a Catmull-Rom makes it overshoot — the visible
  // S-kink just after a bus leaves the lot.
  // Full 3D distance: the decompile squares all three components before the
  // sqrt. It matters, because the join drops a whole unit in Y over a fraction
  // of a unit of ground — measuring that stretch in plan view alone would
  // under-count it and starve it of samples.
  const cumulative = [0]
  for (let i = 1; i < raw.length; i++) {
    cumulative.push(cumulative[i - 1] + raw[i].distanceTo(raw[i - 1]))
  }
  const total = cumulative[cumulative.length - 1]
  const joinArc = cumulative[joinRawIndex]

  if (total < 1e-4) return new EntryPath(raw, joinRawIndex, roadJoinDistance, join.debug)

  // `count = Mathf.Max(4, Mathf.RoundToInt(total / 0.9f))` — the decompile
  // rounds `total / 0.9` (via `modf`, banker's rounding) and floors the result
  // at 4.
  const count = Math.max(4, roundHalfToEven(total / RESAMPLE_SPACING))
  const resampled: Vector3[] = []
  let joinControlIndex = 0
  let bestJoinError = Infinity

  for (let i = 0; i <= count; i++) {
    const target = (total * i) / count
    // Advance to the segment containing `target`.
    let seg = 1
    while (seg < cumulative.length - 1 && cumulative[seg] < target) seg++
    const span = cumulative[seg] - cumulative[seg - 1]
    const t = span < 1e-6 ? 0 : (target - cumulative[seg - 1]) / span
    resampled.push(new Vector3().lerpVectors(raw[seg - 1], raw[seg], t))

    const error = Math.abs(target - joinArc)
    if (error < bestJoinError) {
      bestJoinError = error
      joinControlIndex = i
    }
  }

  return new EntryPath(resampled, joinControlIndex, roadJoinDistance, join.debug)
}

/** `Mathf.RoundToInt` — ties go to the even integer, as the decompile does. */
function roundHalfToEven(value: number): number {
  const floor = Math.floor(value)
  const frac = value - floor
  if (frac > 0.5) return floor + 1
  if (frac < 0.5) return floor
  return floor % 2 === 0 ? floor : floor + 1
}
